import { Branch } from "./useExcludedBranch";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;

export type NodeWithBranch = QuickFilterNodeDTO & { branch: Branch };

export type DepthOption = {
	depth: number;
	count: number;
	examples: string[];
	sameAsPrevious: boolean;
};

export const isPaddedChild = (
	parent: QuickFilterNodeDTO,
	child: QuickFilterNodeDTO,
	siblingCount: number
): boolean => siblingCount === 1 && child.label === parent.label;

export const trueDepthBelow = (node: QuickFilterNodeDTO): number => {
	const children = node?.children ?? [];

	if (children.length === 0) {
		return 0;
	}

	if (isPaddedChild(node, children[0], children.length)) {
		return trueDepthBelow(children[0]);
	}

	return 1 + Math.max(...children.map((child) => trueDepthBelow(child)));
};

export const descendantsAtDepth = (
	node: QuickFilterNodeDTO,
	depth: number,
	branch: Branch = [],
	isExcluded: (branch: Branch) => boolean = () => false
): NodeWithBranch[] => {
	if (isExcluded(branch)) {
		return [];
	}

	const children = node?.children ?? [];

	if (depth <= 1) {
		return children
			.map((child, index) => ({ ...child, branch: branch.concat(index) }))
			.filter((child) => !isExcluded(child.branch));
	}

	return children.reduce(
		(accumulator: NodeWithBranch[], child, index) =>
			accumulator.concat(
				descendantsAtDepth(child, depth - 1, branch.concat(index), isExcluded)
			),
		[]
	);
};

const distinctByLabelAndLevel = (nodes: NodeWithBranch[]): NodeWithBranch[] => [
	...new Map(
		nodes.map((node) => [`${node.label}_${node.level}`, node])
	).values(),
];

const labelKeys = (nodes: NodeWithBranch[]): string =>
	nodes
		.map((node) => `${node.label}_${node.level}`)
		.sort()
		.join("|");

export const buildDepthOptions = (
	scope: QuickFilterNodeDTO,
	{
		isExcluded = () => false,
		exampleLimit = 3,
		scopeBranch = [],
	}: {
		isExcluded?: (branch: Branch) => boolean;
		exampleLimit?: number;

		scopeBranch?: Branch;
	} = {}
): DepthOption[] => {
	const maxDepth = trueDepthBelow(scope);
	const options: DepthOption[] = [];
	let previousKeys: string | null = null;

	for (let depth = 1; depth <= maxDepth; depth++) {
		const nodes = distinctByLabelAndLevel(
			descendantsAtDepth(scope, depth, scopeBranch, isExcluded)
		);
		const keys = labelKeys(nodes);

		options.push({
			depth,
			count: nodes.length,
			examples: nodes.slice(0, exampleLimit).map((node) => node.visibleLabel),
			sameAsPrevious: previousKeys !== null && previousKeys === keys,
		});

		previousKeys = keys;
	}

	return options;
};

export const clampDepth = (
	scope: QuickFilterNodeDTO,
	depth: number | null
): number => {
	const maxDepth = trueDepthBelow(scope);

	if (maxDepth === 0) {
		return 1;
	}

	return Math.min(Math.max(depth ?? 1, 1), maxDepth);
};
